/**
 * `<OwnerEntitySettings>` — designate which person entity is "me".
 *
 * Re-added 2026-05-26. This control had been removed (2026-05-21), which left users with no
 * way to set the owner entity: `whats_on_my_plate` / `get_action_items(assignee="me")` resolve
 * "me" against `app_settings['owner_entity_id']`, NOT the display name. With no UI, "me" stayed
 * unset (cold corpus) and "my plate" was empty.
 *
 * Distinct from `<OwnerUserNameSettings>`:
 *   - This sets the `entities.id` that `assignee="me"` resolves to — a CORPUS-level fact.
 *   - The name panel sets a free-text string for LLM-prompt personalization only.
 *
 * Storage: `app_settings['owner_entity_id']`, written via the existing settings PUT
 * (`SettingsUpdate.ownerEntityId`).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Input } from '@/components/ui';
import { getEntity, listEntities } from '@/api';
import { asEntityId } from '@/types';
import { SettingsCard } from './SettingsCard';

interface OwnerEntitySettingsProps {
  /** The currently-designated owner entity id, or `null` if unset. */
  ownerEntityId: string | null;
  /** Called when the user picks an entity (or clears with `null`). */
  onSave: (entityId: string | null) => void;
  /** True while a save is in flight. */
  saving: boolean;
}

export function OwnerEntitySettings({
  ownerEntityId,
  onSave,
  saving,
}: OwnerEntitySettingsProps): JSX.Element {
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  // Resolve the current owner's display name (best-effort).
  const currentQuery = useQuery({
    queryKey: ['settings', 'owner-entity', ownerEntityId ?? '__none__'],
    enabled: ownerEntityId !== null,
    queryFn: async () => (await getEntity({ id: asEntityId(ownerEntityId as string) })).data,
  });
  const currentName = currentQuery.data?.entity.canonical ?? null;

  // Search people by prefix (most-mentioned first).
  const searchQuery = useQuery({
    queryKey: ['settings', 'owner-entity-search', trimmed],
    enabled: trimmed.length >= 2,
    queryFn: async () =>
      (await listEntities({ prefix: trimmed, types: ['person'], sort: 'count', limit: 8 })).data,
  });
  const results = searchQuery.data?.items ?? [];

  return (
    <SettingsCard
      id="owner-entity"
      title="Who is &ldquo;me&rdquo;?"
      desc={
        <>
          The person &quot;what&apos;s on my plate&quot; resolves to — search your people
          and pick yourself.
        </>
      }
    >
      {ownerEntityId !== null && (
        <Badge variant="default">
          Current: {currentName ?? ownerEntityId}
        </Badge>
      )}
      <div className="flex items-center gap-2">
        <Input
          value={query}
          placeholder="Search people…"
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1"
        />
        {ownerEntityId !== null && (
          <Button size="sm" disabled={saving} onClick={() => onSave(null)}>
            Clear
          </Button>
        )}
      </div>
      {trimmed.length >= 2 && (
        <div className="owner-entity-results">
          {/* U4: distinguish in-flight and failed searches from a genuine empty
              result — previously "No people match" flashed while fetching. */}
          {searchQuery.isFetching ? (
            <div className="settings-hint">Searching…</div>
          ) : searchQuery.isError ? (
            <div className="settings-hint">
              Search failed — check the backend connection, then edit the query to retry.
            </div>
          ) : results.length === 0 ? (
            <div className="settings-hint">No people match &ldquo;{trimmed}&rdquo;.</div>
          ) : (
            results.map((r) => (
              <button
                key={r.entityId}
                type="button"
                className="owner-entity-result"
                disabled={saving}
                onClick={() => {
                  onSave(r.entityId);
                  setQuery('');
                }}
              >
                <span className="owner-entity-result__name">{r.canonical}</span>
                <span className="owner-entity-result__count">{r.noteCount}</span>
                {r.entityId === ownerEntityId && (
                  <span className="owner-entity-result__current">✓ current</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </SettingsCard>
  );
}
